import type { DashboardFilters, TimeRange } from '@delivery-metrics/shared';
import { useQueryClient } from '@tanstack/react-query';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

import { QUERY_KEYS } from '../hooks/useDashboardData';
import { useErrorHandler } from '../hooks/useErrorHandler';
import { useRealTimeUpdates } from '../hooks/useRealTimeUpdates';

interface DataContextType {
  // Global filters
  globalFilters: DashboardFilters;
  setGlobalFilters: (filters: DashboardFilters) => void;
  updateTimeRange: (timeRange: TimeRange) => void;
  updateTeamFilter: (teams: string[]) => void;
  updateProgramFilter: (programs: string[]) => void;
  updateRepositoryFilter: (repositories: string[]) => void;

  // Real-time connection status
  isRealTimeConnected: boolean;
  realTimeError: string | null;
  reconnectRealTime: () => void;

  // Manual refresh
  refreshAllData: () => Promise<void>;
  isRefreshing: boolean;

  // Error handling
  hasErrors: boolean;
  clearErrors: () => void;
  retryFailedQueries: () => void;

  // Data freshness
  lastUpdated: Date | null;
  setLastUpdated: (date: Date) => void;
}

const DataContext = createContext<DataContextType | undefined>(undefined);

interface DataProviderProps {
  children: ReactNode;
}

// Default filters
const getDefaultFilters = (): DashboardFilters => ({
  timeRange: {
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // Last 30 days
    end: new Date(),
    preset: 'last30days',
  },
  teams: [],
  repositories: [],
  programs: [],
});

export const DataProvider: React.FC<DataProviderProps> = ({ children }) => {
  const [globalFilters, setGlobalFilters] =
    useState<DashboardFilters>(getDefaultFilters());
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const queryClient = useQueryClient();
  const { hasErrors, clearErrors, retryLastFailedQuery } = useErrorHandler({
    onError: error => {
      console.error('Data Context Error:', error);
    },
  });

  // Real-time updates - disabled when using mock data
  const {
    isConnected: isRealTimeConnected,
    connectionError: realTimeError,
    reconnect: reconnectRealTime,
  } = useRealTimeUpdates({
    enabled: false, // Disabled - backend not required when using mock data
    onEvent: event => {
      console.log('Real-time event received:', event);
      setLastUpdated(new Date());
    },
    onError: () => {
      // Silently ignore real-time connection errors since backend may not be running
      // This is expected when using mock data
    },
  });

  // Update global filters
  const handleSetGlobalFilters = useCallback((filters: DashboardFilters) => {
    setGlobalFilters(filters);
    setLastUpdated(new Date());
  }, []);

  // Update specific filter parts
  const updateTimeRange = useCallback((timeRange: TimeRange) => {
    setGlobalFilters(prev => ({
      ...prev,
      timeRange,
    }));
    setLastUpdated(new Date());
  }, []);

  const updateTeamFilter = useCallback((teams: string[]) => {
    setGlobalFilters(prev => ({
      ...prev,
      teams,
    }));
    setLastUpdated(new Date());
  }, []);

  const updateProgramFilter = useCallback((programs: string[]) => {
    setGlobalFilters(prev => ({
      ...prev,
      programs,
    }));
    setLastUpdated(new Date());
  }, []);

  const updateRepositoryFilter = useCallback((repositories: string[]) => {
    setGlobalFilters(prev => ({
      ...prev,
      repositories,
    }));
    setLastUpdated(new Date());
  }, []);

  // Manual refresh all data
  const refreshAllData = useCallback(async () => {
    setIsRefreshing(true);
    try {
      // Invalidate all dashboard data
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.LEADERSHIP_METRICS],
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.SCRUM_MASTER_METRICS],
      });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.DEVELOPER_METRICS],
      });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.TEAMS] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROGRAMS] });
      await queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.REPOSITORIES],
      });

      // Wait for queries to refetch
      await queryClient.refetchQueries({
        type: 'all',
        stale: true,
      });

      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to refresh data:', error);
    } finally {
      setIsRefreshing(false);
    }
  }, [queryClient]);

  // Retry failed queries
  const retryFailedQueries = useCallback(() => {
    retryLastFailedQuery();
    setLastUpdated(new Date());
  }, [retryLastFailedQuery]);

  const value: DataContextType = {
    // Global filters
    globalFilters,
    setGlobalFilters: handleSetGlobalFilters,
    updateTimeRange,
    updateTeamFilter,
    updateProgramFilter,
    updateRepositoryFilter,

    // Real-time connection
    isRealTimeConnected,
    realTimeError,
    reconnectRealTime,

    // Manual refresh
    refreshAllData,
    isRefreshing,

    // Error handling
    hasErrors,
    clearErrors,
    retryFailedQueries,

    // Data freshness
    lastUpdated,
    setLastUpdated,
  };

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>;
};

export const useData = (): DataContextType => {
  const context = useContext(DataContext);
  if (context === undefined) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

/**
 * Hook for getting dashboard-specific data with global filters applied
 */
export const useDashboardContext = () => {
  const { globalFilters, lastUpdated, isRefreshing } = useData();

  return {
    filters: globalFilters,
    lastUpdated,
    isRefreshing,
  };
};
